import { Box, Typography } from '@mui/material';

const ARROW_COLOR  = '#2a4a6a';
const ACCENT_COLOR = '#00bcd4';

function Arrow({ x1, y1, x2, y2, color = ARROW_COLOR, dashed = false }) {
  return (
    <line
      x1={x1} y1={y1} x2={x2} y2={y2}
      stroke={color} strokeWidth={1.5}
      strokeDasharray={dashed ? '5,4' : undefined}
      markerEnd={`url(#arr-${color.replace('#', '')})`}
    />
  );
}

function ZoneBox({ x, y, w, h, label, color, fill }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={10} fill={fill} stroke={color} strokeWidth={1.5} strokeDasharray="6 3" opacity={0.9} />
      <text x={x + w / 2} y={y + 18} textAnchor="middle" fill={color} fontSize={11} fontWeight="700" fontFamily="sans-serif" letterSpacing={1.5}>
        {label}
      </text>
    </g>
  );
}

function NodeBox({ x, y, w, h = 36, label, sub, color = '#1e3a5f', textColor = '#ffffff', icon = '' }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={5} fill="#111827" stroke={color} strokeWidth={1.5} />
      <text x={x + (icon ? 22 : w / 2)} y={y + (sub ? h / 2 - 4 : h / 2 + 4)} textAnchor={icon ? 'start' : 'middle'} fill={textColor} fontSize={12} fontWeight="600" fontFamily="monospace">{label}</text>
      {sub && <text x={x + (icon ? 22 : w / 2)} y={y + h / 2 + 10} textAnchor={icon ? 'start' : 'middle'} fill="#4a5a70" fontSize={10} fontFamily="sans-serif">{sub}</text>}
      {icon && <text x={x + 8} y={y + h / 2 + 5} fontSize={13}>{icon}</text>}
    </g>
  );
}

function RouteBox({ x, y, w, label, method = 'GET', color = '#00bcd4' }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={26} rx={4} fill="#0a1525" stroke="#1e3a5f" strokeWidth={1} />
      <rect x={x} y={y} width={34} height={26} rx={4} fill={`${color}22`} stroke="none" />
      <text x={x + 17} y={y + 17} textAnchor="middle" fill={color} fontSize={9} fontWeight="700" fontFamily="monospace">{method}</text>
      <text x={x + 40} y={y + 17} fill="#7a8fa6" fontSize={10} fontFamily="monospace">{label}</text>
    </g>
  );
}

export default function Architecture({ systemId }) {
  const VW = 1160;
  const VH = 700;

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>
      <Box sx={{ marginBottom: 2 }}>
        <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 700 }}>System Architecture</Typography>
        <Typography sx={{ color: '#7a8fa6', fontSize: 13 }}>ASPYN — Full Stack Overview</Typography>
      </Box>

      <Box sx={{ backgroundColor: '#0a0e1a', border: '1px solid #1e3a5f', borderRadius: 2, overflow: 'auto' }}>
        <svg width="100%" viewBox={`0 0 ${VW} ${VH}`} style={{ display: 'block', minWidth: 900 }}>
          <defs>
            {['2a4a6a', '00bcd4', '4caf50', 'a855f7', 'ff9800', 'f44336'].map(c => (
              <marker key={c} id={`arr-${c}`} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                <path d="M0,0 L0,6 L8,3 z" fill={`#${c}`} />
              </marker>
            ))}
          </defs>

          {/* ── Zone backgrounds ── */}
          <ZoneBox x={14}   y={40}  w={175} h={610} label="EXTERNAL INTEGRATIONS" color="#4a5a70"  fill="#0a0e14" />
          <ZoneBox x={210}  y={40}  w={215} h={610} label="FRONTEND · REACT"       color="#00bcd4"  fill="#001418" />
          <ZoneBox x={448}  y={40}  w={235} h={610} label="BACKEND · FASTAPI"      color="#a855f7"  fill="#0d0014" />
          <ZoneBox x={706}  y={40}  w={175} h={280} label="DATABASE · POSTGRESQL"  color="#4caf50"  fill="#001408" />
          <ZoneBox x={706}  y={340} w={175} h={310} label="SECURITY TOOLS"         color="#ff9800"  fill="#140800" />
          <ZoneBox x={904}  y={40}  w={242} h={610} label="CURRENT STATUS"         color="#1e3a5f"  fill="#080c18" />

          {/* ── External integrations ── */}
          <NodeBox x={24}  y={80}  w={155} label="Tenable.io"       sub="Vuln Scanner"        icon="🔍" color="#ff9800" textColor="#ff9800" />
          <NodeBox x={24}  y={130} w={155} label="Cisco ISE"         sub="NAC / C2C"            icon="🔌" color="#00bcd4" textColor="#00bcd4" />
          <NodeBox x={24}  y={180} w={155} label="SIEM Platform"     sub="Log Aggregation"      icon="📊" color="#f44336" textColor="#f44336" />
          <NodeBox x={24}  y={230} w={155} label="Active Directory"  sub="Identity / LDAP"      icon="👤" color="#a855f7" textColor="#a855f7" />
          <NodeBox x={24}  y={280} w={155} label="Firewall / NGFW"   sub="Policy & Logs"        icon="🔥" color="#f44336" textColor="#f44336" />
          <NodeBox x={24}  y={480} w={155} label="Claude API"        sub="Anthropic LLM"        icon="🤖" color="#00bcd4" textColor="#00bcd4" />
          <NodeBox x={24}  y={530} w={155} label="ACAS / SCAP"       sub="DoD Scanner"          icon="🛡️" color="#ff9800" textColor="#ff9800" />
          <NodeBox x={24}  y={580} w={155} label="EMASS"             sub="RMF Tracking"         icon="📋" color="#4caf50" textColor="#4caf50" />

          {/* ── Frontend pages ── */}
          <NodeBox x={220} y={75}  w={195} label="Dashboard"         icon="📊" color={ACCENT_COLOR} />
          <NodeBox x={220} y={115} w={195} label="Assets / IP Range" icon="💾" color={ACCENT_COLOR} />
          <NodeBox x={220} y={155} w={195} label="Vulnerabilities"   icon="🐛" color="#ff9800" />
          <NodeBox x={220} y={195} w={195} label="STIG Compliance"   icon="✅" color="#4caf50" />
          <NodeBox x={220} y={235} w={195} label="Alerts"            icon="🚨" color="#f44336" />
          <NodeBox x={220} y={275} w={195} label="Comply to Connect" icon="🔌" color={ACCENT_COLOR} />
          <NodeBox x={220} y={315} w={195} label="ATO / RMF"         icon="📜" color="#a855f7" />
          <NodeBox x={220} y={355} w={195} label="ATO Export"        icon="📤" color="#4caf50" />
          <NodeBox x={220} y={395} w={195} label="Topology"          icon="🗺️" color={ACCENT_COLOR} />
          <NodeBox x={220} y={435} w={195} label="AI Assistant"      icon="🤖" color={ACCENT_COLOR} />
          <NodeBox x={220} y={530} w={195} h={44} label="React + MUI + ReactFlow" sub="TypeScript / JSX" color="#1e3a5f" textColor="#7a8fa6" />
          <NodeBox x={220} y={580} w={195} h={44} label="dnd-kit · Recharts" sub="DnD / Visualization" color="#1e3a5f" textColor="#7a8fa6" />

          {/* ── Backend API routes ── */}
          <NodeBox x={458} y={75}  w={215} h={44} label="FastAPI Server"   sub="Python 3.11 · Uvicorn" color="#a855f7" textColor="#a855f7" />
          <RouteBox x={458} y={132} w={215} label="/api/assets"       method="GET"  color={ACCENT_COLOR} />
          <RouteBox x={458} y={162} w={215} label="/api/assets/{ip}"  method="PUT"  color="#ff9800" />
          <RouteBox x={458} y={192} w={215} label="/api/vulns"        method="GET"  color={ACCENT_COLOR} />
          <RouteBox x={458} y={222} w={215} label="/api/stig"         method="GET"  color={ACCENT_COLOR} />
          <RouteBox x={458} y={252} w={215} label="/api/alerts"       method="GET"  color={ACCENT_COLOR} />
          <RouteBox x={458} y={282} w={215} label="/api/c2c"          method="GET"  color={ACCENT_COLOR} />
          <RouteBox x={458} y={312} w={215} label="/api/ato"          method="GET"  color={ACCENT_COLOR} />
          <RouteBox x={458} y={342} w={215} label="/api/export/{fmt}" method="POST" color="#4caf50" />
          <RouteBox x={458} y={372} w={215} label="/api/ai/query"     method="POST" color="#a855f7" />
          <RouteBox x={458} y={402} w={215} label="/api/auth/login"   method="POST" color="#f44336" />
          <NodeBox x={458} y={448} w={215} h={44} label="JWT Auth Middleware"   sub="OAuth2 / CAC Support" color="#f44336" textColor="#f44336" />
          <NodeBox x={458} y={498} w={215} h={44} label="RAG Pipeline"         sub="Embeddings · ChromaDB" color="#a855f7" textColor="#a855f7" />
          <NodeBox x={458} y={548} w={215} h={44} label="Data Ingestion Layer" sub="Tenable · ISE · SIEM" color="#ff9800" textColor="#ff9800" />
          <NodeBox x={458} y={598} w={215} h={38} label="Pydantic Schemas · SQLAlchemy" sub="" color="#1e3a5f" textColor="#4a5a70" />

          {/* ── Database ── */}
          <NodeBox x={716} y={80}  w={155} label="assets"         sub="IP · hostname · OS" color="#4caf50" textColor="#4caf50" />
          <NodeBox x={716} y={120} w={155} label="vulnerabilities" sub="CVE · severity"    color="#4caf50" textColor="#4caf50" />
          <NodeBox x={716} y={160} w={155} label="stig_findings"  sub="CAT · control"     color="#4caf50" textColor="#4caf50" />
          <NodeBox x={716} y={200} w={155} label="alerts"         sub="severity · time"   color="#4caf50" textColor="#4caf50" />
          <NodeBox x={716} y={240} w={155} label="ato_controls"   sub="status · family"   color="#4caf50" textColor="#4caf50" />
          <NodeBox x={716} y={280} w={155} label="users / sessions" sub="auth · roles"    color="#4caf50" textColor="#4caf50" />

          {/* ── Security Tools ── */}
          <NodeBox x={716} y={382} w={155} label="Nessus / ACAS"   sub="Scan results API"  color="#ff9800" textColor="#ff9800" />
          <NodeBox x={716} y={422} w={155} label="ISE REST API"    sub="Posture · Policy"  color="#ff9800" textColor="#ff9800" />
          <NodeBox x={716} y={462} w={155} label="Syslog / CEF"    sub="SIEM log ingestion" color="#ff9800" textColor="#ff9800" />
          <NodeBox x={716} y={502} w={155} label="LDAP / AD"       sub="User directory"    color="#ff9800" textColor="#ff9800" />
          <NodeBox x={716} y={542} w={155} label="SCAP / OpenSCAP" sub="STIG automation"   color="#ff9800" textColor="#ff9800" />
          <NodeBox x={716} y={582} w={155} label="EMASS API"       sub="RMF package sync"  color="#ff9800" textColor="#ff9800" />

          {/* ── Status column ── */}
          {[
            { label: '✅ Implemented',  sub: 'Dashboard, Assets, Topology',       color: '#4caf50', y: 75  },
            { label: '✅ Implemented',  sub: 'Vulns, STIG, Alerts, C2C, ATO',     color: '#4caf50', y: 125 },
            { label: '✅ Implemented',  sub: 'AI Assistant (mock RAG)',             color: '#4caf50', y: 175 },
            { label: '✅ Implemented',  sub: 'ATO Export, Topology Drill-down',    color: '#4caf50', y: 225 },
            { label: '🔧 In Progress',  sub: 'FastAPI backend scaffolding',        color: '#ff9800', y: 305 },
            { label: '🔧 In Progress',  sub: 'PostgreSQL schema design',           color: '#ff9800', y: 355 },
            { label: '📋 Planned',      sub: 'Claude RAG integration',             color: '#a855f7', y: 435 },
            { label: '📋 Planned',      sub: 'Tenable / ISE data ingestion',       color: '#a855f7', y: 485 },
            { label: '📋 Planned',      sub: 'CAC / JWT authentication',           color: '#a855f7', y: 535 },
            { label: '📋 Planned',      sub: 'EMASS & ACAS live sync',             color: '#a855f7', y: 585 },
          ].map(({ label, sub, color, y }) => (
            <g key={y}>
              <text x={914} y={y + 15} fill={color} fontSize={12} fontWeight="700" fontFamily="sans-serif">{label}</text>
              <text x={914} y={y + 29} fill="#4a5a70" fontSize={11} fontFamily="sans-serif">{sub}</text>
            </g>
          ))}

          {/* ── Dividers in status column ── */}
          <line x1={914} y1={278} x2={1136} y2={278} stroke="#1e3a5f" strokeWidth={1} strokeDasharray="4 3" />
          <line x1={914} y1={410} x2={1136} y2={410} stroke="#1e3a5f" strokeWidth={1} strokeDasharray="4 3" />
          <text x={914} y={295} fill="#2a4a6a" fontSize={10} fontFamily="sans-serif" fontWeight="700">── BACKEND ──────────────────────</text>
          <text x={914} y={425} fill="#2a4a6a" fontSize={10} fontFamily="sans-serif" fontWeight="700">── INTEGRATIONS ─────────────────</text>

          {/* ── Arrows: Frontend → Backend ── */}
          <Arrow x1={415} y1={260} x2={448} y2={260} color="#00bcd4" />
          <text x={427} y={256} fill="#00bcd4" fontSize={9} fontFamily="monospace">REST</text>

          {/* ── Arrows: Backend → Database ── */}
          <Arrow x1={673} y1={200} x2={706} y2={200} color="#4caf50" />
          <text x={678} y={196} fill="#4caf50" fontSize={9} fontFamily="monospace">SQL</text>

          {/* ── Arrows: Backend → Security Tools ── */}
          <Arrow x1={673} y1={490} x2={706} y2={490} color="#ff9800" />
          <text x={676} y={486} fill="#ff9800" fontSize={9} fontFamily="monospace">API</text>

          {/* ── Arrows: External → Backend ── */}
          <Arrow x1={179} y1={103} x2={458} y2={530} color="#4a5a70" dashed />
          <Arrow x1={179} y1={153} x2={458} y2={556} color="#4a5a70" dashed />
          <Arrow x1={179} y1={203} x2={458} y2={568} color="#4a5a70" dashed />

          {/* ── Arrows: Claude → Backend ── */}
          <Arrow x1={179} y1={498} x2={458} y2={498} color="#a855f7" dashed />

          {/* ── Legend ── */}
          <rect x={14} y={644} width={870} height={46} rx={6} fill="#0d1117" stroke="#1e3a5f" strokeWidth={1} />
          {[
            { color: '#00bcd4', label: 'REST API call' },
            { color: '#4caf50', label: 'SQL / ORM' },
            { color: '#a855f7', label: 'LLM / AI' },
            { color: '#ff9800', label: 'Security tool integration' },
            { color: '#4a5a70', dash: true, label: 'Data ingestion (planned)' },
          ].map(({ color, label, dash }, i) => (
            <g key={label}>
              <line x1={28 + i * 178} y1={667} x2={60 + i * 178} y2={667} stroke={color} strokeWidth={1.5} strokeDasharray={dash ? '5,4' : undefined} />
              <circle cx={44 + i * 178} cy={667} r={3} fill={color} />
              <text x={66 + i * 178} y={671} fill="#7a8fa6" fontSize={11} fontFamily="sans-serif">{label}</text>
            </g>
          ))}
        </svg>
      </Box>
    </Box>
  );
}
